import { getRouteApi } from '@tanstack/react-router';

import { useState } from 'react';
import { ClanSelector, CountrySelector } from '~/components/ItemSelector';
import Flag from '~/partials/Flag';
import { Button } from '~/primitives/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '~/primitives/dialog';
import { RadioGroup, RadioGroupItem } from '~/primitives/radiogroup';
import { fromNumeric, getCountryName } from '~/util/countries';
import { useResettableState } from '~/util/hooks/useResettableState';

const Route = getRouteApi('/leaderboard');

interface LeaderboardSettingsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const LeaderboardSettingsModal: React.FC<LeaderboardSettingsModalProps> = ({
    isOpen,
    onClose,
}) => {
    const searchParams = Route.useSearch();
    const navigate = Route.useNavigate();

    const [localSearchParams, setLocalSearchParams] =
        useResettableState(searchParams);

    const [isClanSelectorOpen, setClanSelectorOpen] = useState(false);
    const [isCountrySelectorOpen, setCountrySelectorOpen] = useState(false);

    const applySettings = () => {
        navigate({
            search: {
                ...localSearchParams,
                search: '',
                page: 1,
            },
            replace: true,
        });

        onClose();
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="p-8" aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>Leaderboard Settings</DialogTitle>
                </DialogHeader>
                <div className="grid lg:grid-flow-col gap-2 lg:gap-12 mt-4">
                    <div>
                        <h3 className="text-xl font-semibold mb-2">Sort By</h3>
                        <div className="flex flex-col space-y-2">
                            <RadioGroup
                                value={localSearchParams.sort}
                                onValueChange={(
                                    value:
                                        | 'TotalPoints'
                                        | 'RawPoints'
                                        | 'ExtremeCount',
                                ) =>
                                    setLocalSearchParams((prev) => ({
                                        ...prev,
                                        sort: value,
                                    }))
                                }
                                aria-labelledby="sort-label"
                                className="bg-background-dark h-[30vh] p-4 rounded-xl"
                            >
                                <RadioGroupItem
                                    value="TotalPoints"
                                    id="totalpoints"
                                    label="Points (with packs)"
                                />
                                <RadioGroupItem
                                    value="RawPoints"
                                    id="rawpoints"
                                    label="Points"
                                />
                                <RadioGroupItem
                                    value="ExtremeCount"
                                    id="extremecount"
                                    label="Extremes Count"
                                />
                                <RadioGroupItem
                                    value="Hardest"
                                    id="hardest"
                                    label="Hardest Level"
                                />
                            </RadioGroup>
                        </div>
                    </div>

                    {localSearchParams.type === 'users' && (
                        <>
                            <div>
                                <h3 className="text-xl font-semibold mb-2 ">
                                    Selected Country
                                </h3>
                                <Button
                                    variant="outline"
                                    onClick={() => setCountrySelectorOpen(true)}
                                >
                                    <span className="text-secondary-foreground flex gap-2 items-center">
                                        {localSearchParams.country === 0 ? (
                                            '🌍 Global'
                                        ) : (
                                            <>
                                                <Flag
                                                    countryCode={
                                                        localSearchParams.country
                                                    }
                                                />
                                                {getCountryName(
                                                    fromNumeric(
                                                        localSearchParams.country,
                                                    ),
                                                ) || 'Unknown country'}
                                            </>
                                        )}
                                    </span>
                                </Button>
                                <CountrySelector
                                    single
                                    open={isCountrySelectorOpen}
                                    onOpenChange={() =>
                                        setCountrySelectorOpen(false)
                                    }
                                    initial={
                                        localSearchParams.country === 0
                                            ? undefined
                                            : fromNumeric(
                                                  localSearchParams.country,
                                              )
                                    }
                                    onSave={(selectedCountry) => {
                                        setLocalSearchParams((prev) => ({
                                            ...prev,
                                            country:
                                                Number(
                                                    selectedCountry?.numeric,
                                                ) || 0,
                                        }));
                                        setCountrySelectorOpen(false);
                                    }}
                                />
                            </div>
                            <div>
                                <h3 className="text-xl font-semibold mb-2 ">
                                    Selected Clan
                                </h3>
                                <Button
                                    variant="outline"
                                    onClick={() => setClanSelectorOpen(true)}
                                >
                                    <span className="text-secondary-foreground flex gap-2 items-center">
                                        {localSearchParams.clan === null
                                            ? 'None'
                                            : localSearchParams.clan?.tag ||
                                              'Unknown'}
                                    </span>
                                </Button>
                                <ClanSelector
                                    open={isClanSelectorOpen}
                                    single
                                    onOpenChange={() =>
                                        setClanSelectorOpen(false)
                                    }
                                    tagOptions={{ showFullName: true }}
                                    initial={
                                        localSearchParams.clan ?? undefined
                                    }
                                    onSave={(selectedClan) => {
                                        setLocalSearchParams((prev) => ({
                                            ...prev,
                                            clan: selectedClan ?? null,
                                        }));
                                        setClanSelectorOpen(false);
                                    }}
                                />
                            </div>
                        </>
                    )}
                </div>
                <DialogFooter>
                    <Button onClick={applySettings}>Apply</Button>
                    <Button variant="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export { LeaderboardSettingsModal };
